import React from 'react';
import { StatusPill } from '../systemKit';
import { fmtCompact, fmtFull, fmtSignedPct, deltaColor, deltaPillTone } from './utils';

// TopMovers — purely presentational. Renders a ranked list of price-mover
// rows. Owner of this component is responsible for fetching rows (via the
// cost_dashboard_top_impact_movers RPC), applying vendor/polarity filters
// and sort, and providing the final rows as a `rows` prop.
//
// Pre-restructure this component fetched + sorted internally and lived
// inside the Cost Dashboard's section composition. The Cost Dashboard
// no longer carries this section — it lives in the Reports tab on the
// Invoices page. Decoupling fetch from render keeps the Reports tab's
// filter/sort flow simple.

export default function TopMovers({
  rows,
  loading,
  onOpenDrillIn,
  capNoticeText,
}) {
  return (
    <div>
      {loading && <Skeleton />}
      {!loading && rows && rows.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          Not enough comparable price data yet — try a different time range.
        </div>
      )}
      {!loading && rows && rows.length > 0 && (
        <>
          <div style={{ borderTop: '1px solid var(--border)' }}>
            {rows.map(r => (
              <MoverRow
                key={r.food_item_id}
                row={r}
                onOpen={() => onOpenDrillIn?.(r)}
              />
            ))}
          </div>
          {capNoticeText && (
            <div style={{ padding: 10, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
              {capNoticeText}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MoverRow({ row, onOpen }) {
  const tone = deltaPillTone(row.delta_pct, row.trend_direction);
  const color = deltaColor(row.delta_pct, row.trend_direction);
  const dollar = Number(row.dollar_impact || 0);
  const ci = Number(row.consecutive_increases || 0);

  return (
    <div
      onClick={onOpen}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 2fr) 130px 100px 110px 70px',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        transition: 'background 120ms ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.item_name || `Item #${row.food_item_id}`}
          </span>
          {row.on_watchlist && (
            <span title="On watchlist" style={{ color: 'var(--accent-primary)', fontSize: 11 }}>★</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {(row.primary_vendor || '—').replace(/\n/g, ' · ')}
        </div>
      </div>

      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 12, color: 'var(--text-secondary)' }}>
        <div>{row.baseline_unit_price != null ? fmtFull(row.baseline_unit_price) : '—'}</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 10 }}>baseline</div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <StatusPill tone={tone}>
          <span style={{ color }}>{row.delta_pct != null ? fmtSignedPct(row.delta_pct) : '—'}</span>
        </StatusPill>
      </div>

      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        <div style={{
          fontSize: 16, fontWeight: 700,
          color: dollar > 0 ? 'var(--text-critical)' : dollar < 0 ? 'var(--text-success)' : 'var(--text-primary)',
        }}>
          {dollar > 0 ? '+' : ''}{fmtCompact(dollar)}
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          impact
        </div>
      </div>

      <div style={{ textAlign: 'right' }}>
        {ci >= 3 && row.trend_direction === 'up' && <StatusPill tone="red">{ci}×</StatusPill>}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div style={{ borderTop: '1px solid var(--border)' }}>
      {[0, 1, 2, 3].map(i => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '2fr 130px 100px 110px 70px',
          gap: 12, padding: '14px 16px',
          borderBottom: '1px solid var(--border)',
        }}>
          {[0, 1, 2, 3, 4].map(j => (
            <div key={j} style={{ height: 12, background: 'var(--bg-subtle)', borderRadius: 4 }} />
          ))}
        </div>
      ))}
    </div>
  );
}
